import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Button, confirm, toast } from '@devhop/ui';
import { HelpCircle, Plus } from 'lucide-react';
import { Header } from '@/app/layout/Header';
import { Main } from '@/app/layout/Main';
import {
    useDeletePromotionRuleMutation,
    usePromotionPositionsLookupQuery,
    usePromotionRulesQuery,
} from '../api/queries';
import {
    PromotionRulesFilter,
    type PromotionRulesSearchParams,
} from '../ui/PromotionRulesFilter';
import { PromotionRulesTable } from '../ui/PromotionRulesTable';
import type { PromotionPathRuleListItem } from '@/modules/hr/promotion/domain/types';
import {
    PromotionTour,
    promotionRulesTourSteps,
    usePromotionTour,
} from '../tour';

export function PromotionRulesPage() {
    const nav = useNavigate({ from: '/app/hr/promotion/rules' });
    const search = useSearch({
        from: '/app/hr/promotion/rules',
    }) as PromotionRulesSearchParams;

    const sortBy = search.sort?.[0]?.field ?? 'createdAt';
    const sortOrder = (search.sort?.[0]?.dir ?? 'desc') as 'asc' | 'desc';

    const query = useMemo(
        () => ({
            offset: search.offset ?? 0,
            limit: search.limit ?? 20,
            sort: search.sort,
            isActive: search.isActive,
            fromPositionId: search.fromPositionId,
            toPositionId: search.toPositionId,
            search: search.search,
        }),
        [search],
    );

    const rulesQuery = usePromotionRulesQuery(query);
    const positionsQuery = usePromotionPositionsLookupQuery();
    const deleteMutation = useDeletePromotionRuleMutation();

    const rows = rulesQuery.data?.data ?? [];

    const handleDelete = useCallback(
        async (row: PromotionPathRuleListItem) => {
            const ok = await confirm({
                title: 'ລຶບຂໍ້ກຳນົດການເລື່ອນຕຳແໜ່ງ',
                description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ "${row.fromPositionTitle ?? ''} → ${row.toPositionTitle ?? ''}"? ການກະທຳນີ້ບໍ່ສາມາດກູ້ຄືນໄດ້.`,
                actionText: 'ລຶບ',
                ActionProps: { variant: 'destructive' },
            });
            if (!ok) return;
            deleteMutation.mutate(row.id, {
                onSuccess: () => toast.success('ລຶບຂໍ້ກຳນົດສຳເລັດ'),
                onError: (err: { message?: string }) =>
                    toast.error(err?.message ?? 'ລຶບບໍ່ສຳເລັດ'),
            });
        },
        [deleteMutation],
    );

    const handlePage = (offset: number, limit: number) => {
        nav({
            search: {
                offset,
                limit,
                sort: search.sort,
                isActive: search.isActive,
                fromPositionId: search.fromPositionId,
                toPositionId: search.toPositionId,
                search: search.search,
            },
        });
    };

    const handleEdit = (row: PromotionPathRuleListItem) => {
        nav({ to: '/app/hr/promotion/rules/$id/edit', params: { id: row.id } });
    };

    const handleSort = useCallback(
        (id: string, desc: boolean) => {
            const nextSort = [
                {
                    field: id,
                    dir: (desc ? 'desc' : 'asc') as 'asc' | 'desc',
                },
            ];
            nav({
                search: {
                    offset: 0,
                    limit: search.limit ?? 20,
                    sort: nextSort,
                    isActive: search.isActive,
                    fromPositionId: search.fromPositionId,
                    toPositionId: search.toPositionId,
                    search: search.search,
                },
            });
        },
        [nav, search],
    );

    const tour = usePromotionTour('rules');

    return (
        <>
            <Header />
            <Main>
                <PromotionTour
                    steps={promotionRulesTourSteps}
                    run={tour.run}
                    onCallback={tour.handleJoyrideCallback}
                />
                <div
                    className="mb-6 flex items-start justify-between gap-4"
                    data-tourid="promotion-rules-toolbar"
                >
                    <div className="flex items-center gap-2">
                        <h1 className="font-semibold text-2xl">
                            ຂໍ້ກຳນົດການເລື່ອນຕຳແໜ່ງ
                        </h1>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="size-7 rounded-full"
                            onClick={tour.startTour}
                            title="ເລີ່ມທົດສອບການນຳທາງ"
                            aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                        >
                            <HelpCircle className="size-5" />
                        </Button>
                    </div>
                    <Button
                        onClick={() =>
                            nav({ to: '/app/hr/promotion/rules/new' })
                        }
                        className="gap-2"
                    >
                        <Plus className="h-4 w-4" />
                        ສ້າງຂໍ້ກຳນົດການເລື່ອນຕຳແໜ່ງ
                    </Button>
                </div>

                <div
                    className="mb-6 flex flex-col rounded-xl border bg-card pt-2"
                    data-tourid="promotion-rules-filter"
                >
                    <PromotionRulesFilter
                        searchParams={search}
                        fromRoute="/app/hr/promotion/rules"
                        positionOptions={(positionsQuery.data ?? []).map(
                            (p) => ({ value: p.id, label: p.label }),
                        )}
                    />
                    <div data-tourid="promotion-rules-table">
                        <PromotionRulesTable
                        data={rows}
                        isLoading={rulesQuery.isLoading}
                    offset={search.offset ?? 0}
                    limit={search.limit ?? 20}
                    totalCount={rulesQuery.data?.meta.total ?? 0}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortingChange={handleSort}
                    onPaginationChange={handlePage}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    />
                    </div>
                </div>
            </Main>
        </>
    );
}
